import React, { useEffect } from "react";
import {
  ArrowRight,
  Building2,
  CheckCircle2,
  CircleDollarSign,
  ClipboardCheck,
  Flame,
  Home,
  Mail,
  MapPin,
  Phone,
  ShieldCheck,
  Snowflake,
  Volume2,
  Wifi,
  Wind,
  Wrench,
  Zap,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";

const META_DESCRIPTION =
  "Effiziente Klimaanlagen für Stuttgart und Umgebung: Beratung, Planung und Montage von Daikin und Midea. Jetzt unverbindlich bei Gravante Solar anfragen.";

const benefits = [
  {
    icon: Snowflake,
    title: "Angenehm kühlen",
    text: "Konstante Wunschtemperatur auch an heißen Sommertagen.",
  },
  {
    icon: Flame,
    title: "Effizient heizen",
    text: "Viele Geräte arbeiten zusätzlich als moderne Luft-Luft-Wärmepumpe.",
  },
  {
    icon: Volume2,
    title: "Leise genießen",
    text: "Sorgfältige Auslegung für ruhigen Betrieb in Wohn- und Arbeitsräumen.",
  },
  {
    icon: Wifi,
    title: "Smart steuern",
    text: "Temperatur und Betriebszeiten bequem per App kontrollieren.",
  },
];

const solutions = [
  {
    icon: Home,
    eyebrow: "Wohnen",
    title: "Wohnung & Einfamilienhaus",
    text: "Dezente Innengeräte, passende Leistung und sauber geplante Leitungswege für Schlaf-, Wohn- und Dachräume.",
  },
  {
    icon: Building2,
    eyebrow: "Gewerbe",
    title: "Büro, Praxis & Laden",
    text: "Zuverlässige Klimatisierung für Mitarbeitende, Kundschaft und sensible Arbeitsbereiche.",
  },
  {
    icon: Wind,
    eyebrow: "Flexibel",
    title: "Single- & Multi-Split",
    text: "Ein Raum oder mehrere Zonen: Wir wählen die passende Systemgröße für das Gebäude und Ihre Nutzung.",
  },
];

const steps = [
  {
    number: "01",
    title: "Bedarf klären",
    text: "Räume, Nutzung, Komfortwünsche und mögliche Aufstellorte gemeinsam erfassen.",
  },
  {
    number: "02",
    title: "System planen",
    text: "Leistung, Innen- und Außengeräte, Leitungswege, Schallschutz und Elektroanschluss festlegen.",
  },
  {
    number: "03",
    title: "Fachgerecht montieren",
    text: "Saubere Installation, Inbetriebnahme und verständliche Einweisung in Ihre neue Anlage.",
  },
];

const faqs = [
  {
    question: "Was kostet eine Klimaanlage mit Montage in Stuttgart?",
    answer:
      "Die Kosten hängen von Raumgröße, Anzahl der Innengeräte, Leitungswegen, Schallschutz und Montageaufwand ab. Nach einer kurzen Bedarfsanalyse erhalten Sie ein individuelles Angebot; pauschale Festpreise ohne Prüfung wären unseriös.",
  },
  {
    question: "Kann eine Split-Klimaanlage auch heizen?",
    answer:
      "Ja. Viele moderne Split-Klimaanlagen arbeiten als Luft-Luft-Wärmepumpe und können Räume kühlen und heizen. Welche Leistung sinnvoll ist, wird passend zum Gebäude und zur Nutzung ausgelegt.",
  },
  {
    question: "Gibt es eine Förderung für Klimaanlagen?",
    answer:
      "Eine reine Kühlanlage ist in der Regel nicht förderfähig. Eine Luft-Luft-Wärmepumpe kann unter bestimmten Voraussetzungen förderfähig sein, wenn sie überwiegend zum Heizen dient und technische Anforderungen erfüllt. Die Förderfähigkeit muss vor der Beauftragung individuell geprüft werden.",
  },
  {
    question: "Welche Marke passt besser: Daikin oder Midea?",
    answer:
      "Beide Hersteller bieten effiziente Single- und Multi-Split-Lösungen. Die Auswahl richtet sich nach Raumanzahl, Kühl- und Heizlast, Lautstärke, Komfortfunktionen, Budget und gewünschtem Service.",
  },
  {
    question: "Wie laufen Planung und Montage einer Klimaanlage ab?",
    answer:
      "Wir klären Bedarf und Aufstellorte, dimensionieren das System, planen Leitungswege und Elektroanschluss, montieren Innen- und Außengerät und nehmen die Anlage in Betrieb. Dauer und Aufwand hängen vom jeweiligen Objekt ab.",
  },
];

function usePageSeo() {
  useEffect(() => {
    const titleBefore = document.title;
    document.title = "Klimaanlagen Stuttgart | Beratung & Montage | Gravante Solar";

    const upsertMeta = (name, content) => {
      let node = document.head.querySelector(`meta[name="${name}"]`);
      const created = !node;
      if (!node) {
        node = document.createElement("meta");
        node.setAttribute("name", name);
        document.head.appendChild(node);
      }
      const previous = node.getAttribute("content");
      node.setAttribute("content", content);
      return () => {
        if (created) node.remove();
        else if (previous === null) node.removeAttribute("content");
        else node.setAttribute("content", previous);
      };
    };

    const hostname = window.location.hostname.toLowerCase();
    const isProduction = hostname === "gravante.de" || hostname === "www.gravante.de";
    const restoreDescription = upsertMeta("description", META_DESCRIPTION);
    const restoreRobots = upsertMeta(
      "robots",
      isProduction ? "index,follow,max-image-preview:large" : "noindex,nofollow"
    );

    const existingCanonical = document.head.querySelector('link[rel="canonical"]');
    const previousCanonical = existingCanonical?.getAttribute("href") ?? null;
    let canonical = existingCanonical;
    let canonicalCreated = false;
    if (isProduction) {
      if (!canonical) {
        canonical = document.createElement("link");
        canonical.setAttribute("rel", "canonical");
        document.head.appendChild(canonical);
        canonicalCreated = true;
      }
      canonical.setAttribute("href", "https://gravante.de/klimaanlagen");
    } else if (canonical) {
      canonical.remove();
    }

    const schema = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "HVACBusiness",
          "@id": "https://gravante.de/#business",
          name: "Gravante Solar",
          url: "https://gravante.de/",
          telephone: "+4915782283963",
          email: "Buchhaltung@gravante.de",
          address: {
            "@type": "PostalAddress",
            streetAddress: "Am Wammesknopf 6",
            postalCode: "70439",
            addressLocality: "Stuttgart",
            addressCountry: "DE",
          },
          areaServed: { "@type": "City", name: "Stuttgart" },
          openingHoursSpecification: [
            {
              "@type": "OpeningHoursSpecification",
              dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
              opens: "08:00",
              closes: "18:00",
            },
          ],
        },
        {
          "@type": "Service",
          "@id": "https://gravante.de/klimaanlagen#service",
          name: "Klimaanlagen in Stuttgart",
          serviceType: "Beratung, Planung und Montage von Klimaanlagen",
          provider: { "@id": "https://gravante.de/#business" },
          areaServed: { "@type": "City", name: "Stuttgart" },
          url: "https://gravante.de/klimaanlagen",
        },
        {
          "@type": "WebPage",
          "@id": "https://gravante.de/klimaanlagen#webpage",
          url: "https://gravante.de/klimaanlagen",
          name: "Klimaanlagen Stuttgart | Beratung & Montage | Gravante Solar",
          description: META_DESCRIPTION,
          inLanguage: "de-DE",
          mainEntity: { "@id": "https://gravante.de/klimaanlagen#service" },
          about: { "@id": "https://gravante.de/#business" },
        },
      ],
    };

    const jsonLd = document.createElement("script");
    jsonLd.type = "application/ld+json";
    jsonLd.dataset.page = "klimaanlagen";
    jsonLd.text = JSON.stringify(schema);
    document.head.appendChild(jsonLd);

    return () => {
      document.title = titleBefore;
      restoreDescription();
      restoreRobots();
      jsonLd.remove();
      if (canonicalCreated) canonical?.remove();
      else if (isProduction && canonical && previousCanonical !== null) {
        canonical.setAttribute("href", previousCanonical);
      } else if (isProduction && canonical) {
        canonical.removeAttribute("href");
      } else if (!isProduction && existingCanonical) {
        document.head.appendChild(existingCanonical);
      }
    };
  }, []);
}

function useAppleMotion() {
  useEffect(() => {
    const page = document.querySelector(".klima-page");
    if (!page) return undefined;

    const revealItems = Array.from(page.querySelectorAll("[data-klima-reveal]"));
    document.documentElement.classList.add("klima-motion-ready");

    if (!("IntersectionObserver" in window)) {
      revealItems.forEach((item) => item.classList.add("klima-is-visible"));
      return () => document.documentElement.classList.remove("klima-motion-ready");
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("klima-is-visible");
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.14, rootMargin: "0px 0px -8% 0px" }
    );

    revealItems.forEach((item) => observer.observe(item));

    return () => {
      observer.disconnect();
      document.documentElement.classList.remove("klima-motion-ready");
    };
  }, []);
}

const appleMotionStyles = `
  .klima-page {
    --klima-ink: #0a1020;
    --klima-blue: #0787ff;
    background:
      radial-gradient(circle at 50% -8%, rgba(125, 211, 252, .16), transparent 30rem),
      #fff;
  }

  .klima-display {
    letter-spacing: -.055em;
    text-wrap: balance;
  }

  .klima-glass {
    background: linear-gradient(145deg, rgba(255,255,255,.88), rgba(248,250,252,.66));
    border: 1px solid rgba(255,255,255,.9);
    box-shadow: 0 24px 70px rgba(15,23,42,.09), inset 0 1px 0 rgba(255,255,255,.95);
    backdrop-filter: blur(24px) saturate(145%);
    -webkit-backdrop-filter: blur(24px) saturate(145%);
  }

  .klima-dark-glass {
    background: linear-gradient(145deg, rgba(255,255,255,.105), rgba(255,255,255,.035));
    border: 1px solid rgba(255,255,255,.13);
    box-shadow: inset 0 1px 0 rgba(255,255,255,.12), 0 30px 90px rgba(2,6,23,.28);
    backdrop-filter: blur(24px) saturate(135%);
    -webkit-backdrop-filter: blur(24px) saturate(135%);
  }

  .klima-hero-orb {
    animation: klima-orbit 13s ease-in-out infinite alternate;
    will-change: transform;
  }

  .klima-hero-orb-slow {
    animation-duration: 18s;
    animation-direction: alternate-reverse;
  }

  .klima-unit {
    animation: klima-float 6.5s ease-in-out infinite;
    transform-origin: 50% 60%;
    will-change: transform;
  }

  .klima-air-line {
    position: absolute;
    left: 50%;
    top: calc(100% - 2px);
    height: 8.5rem;
    width: 5.5rem;
    border-left: 2px solid rgba(14,165,233,.34);
    border-radius: 50%;
    filter: blur(.15px);
    opacity: .72;
    transform-origin: top center;
    animation: klima-air 3.2s ease-in-out infinite;
  }

  .klima-air-line:nth-child(2) {
    margin-left: -4rem;
    animation-delay: -.9s;
  }

  .klima-air-line:nth-child(3) {
    margin-left: 3.1rem;
    animation-delay: -1.7s;
  }

  .klima-media-card img {
    transition: transform 1.15s cubic-bezier(.22,1,.36,1), filter .8s ease;
  }

  .klima-media-card:hover img {
    transform: scale(1.025);
    filter: saturate(1.04);
  }

  .klima-lift {
    transition: transform .55s cubic-bezier(.22,1,.36,1), border-color .4s ease, box-shadow .55s ease, background-color .4s ease;
  }

  .klima-lift:hover {
    transform: translateY(-8px);
    box-shadow: 0 26px 72px rgba(15,23,42,.12);
  }

  .klima-button {
    transition: transform .35s cubic-bezier(.22,1,.36,1), box-shadow .35s ease, background-color .25s ease;
  }

  .klima-button:hover {
    transform: translateY(-2px) scale(1.015);
  }

  .klima-motion-ready [data-klima-reveal] {
    opacity: 0;
    filter: blur(8px);
    transform: translate3d(0, 42px, 0) scale(.985);
    transition:
      opacity .9s cubic-bezier(.22,1,.36,1),
      transform 1.05s cubic-bezier(.22,1,.36,1),
      filter .9s ease;
    transition-delay: var(--klima-delay, 0ms);
    will-change: opacity, transform, filter;
  }

  .klima-motion-ready [data-klima-reveal="left"] {
    transform: translate3d(-42px, 0, 0) scale(.985);
  }

  .klima-motion-ready [data-klima-reveal="right"] {
    transform: translate3d(42px, 0, 0) scale(.985);
  }

  .klima-motion-ready [data-klima-reveal].klima-is-visible {
    opacity: 1;
    filter: blur(0);
    transform: translate3d(0, 0, 0) scale(1);
    will-change: auto;
  }

  @keyframes klima-orbit {
    from { transform: translate3d(-2%, -2%, 0) scale(.96); }
    to { transform: translate3d(8%, 6%, 0) scale(1.08); }
  }

  @keyframes klima-float {
    0%, 100% { transform: translate3d(0, 0, 0) rotate(-.4deg); }
    50% { transform: translate3d(0, -12px, 0) rotate(.4deg); }
  }

  @keyframes klima-air {
    0%, 100% { opacity: .2; transform: translateX(-50%) rotate(10deg) scaleY(.72); }
    50% { opacity: .78; transform: translateX(-50%) rotate(-8deg) scaleY(1.08); }
  }

  @media (max-width: 640px) {
    .klima-display { letter-spacing: -.042em; }
    .klima-air-line { height: 6rem; }
  }

  @media (prefers-reduced-motion: reduce) {
    .klima-page *,
    .klima-page *::before,
    .klima-page *::after {
      scroll-behavior: auto !important;
      animation-duration: .01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: .01ms !important;
      transition-delay: 0ms !important;
    }

    .klima-motion-ready [data-klima-reveal] {
      opacity: 1;
      filter: none;
      transform: none;
    }
  }
`;

const SectionHeading = ({ eyebrow, title, text, light = false }) => (
  <div className="mx-auto mb-16 max-w-4xl text-center" data-klima-reveal>
    <p className={`mb-3 text-sm font-bold uppercase tracking-[0.22em] ${light ? "text-cyan-300" : "text-sky-600"}`}>
      {eyebrow}
    </p>
    <h2 className={`klima-display text-4xl font-black leading-[1.02] sm:text-5xl lg:text-6xl ${light ? "text-white" : "text-slate-950"}`}>
      {title}
    </h2>
    {text && <p className={`mt-4 text-lg leading-8 ${light ? "text-slate-300" : "text-slate-600"}`}>{text}</p>}
  </div>
);

export default function KlimaanlagenPage() {
  usePageSeo();
  useAppleMotion();

  return (
    <div className="klima-page min-h-screen overflow-hidden bg-white text-slate-900">
      <style>{appleMotionStyles}</style>
      <Header />
      <main>
        <section
          className="relative overflow-hidden bg-[#05070d]"
          aria-label="Klimaanlage in Bewegung"
          data-klima-autoplay-video
        >
          <video
            className="block max-h-[82vh] min-h-[420px] w-full object-cover object-center brightness-[1.4] contrast-[0.92] saturate-[1.08] sm:min-h-[560px]"
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            disablePictureInPicture
            onLoadedData={(event) => {
              const video = event.currentTarget;
              video.muted = true;
              video.play().catch(() => {});
            }}
            onCanPlay={(event) => event.currentTarget.play().catch(() => {})}
            aria-label="Moderne Klimaanlage von Daikin und Midea"
          >
            <source src="/waermepumpe-musik-final.mp4" type="video/mp4" />
            Ihr Browser unterstützt dieses Video nicht.
          </video>
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-black/5" />
          <div className="pointer-events-none absolute inset-x-0 bottom-0 mx-auto max-w-7xl px-6 pb-10 text-white sm:px-10 sm:pb-14">
            <p className="text-xs font-black uppercase tracking-[0.28em] text-cyan-300 sm:text-sm">
              Daikin · Midea · Komfort in Bewegung
            </p>
            <p className="klima-display mt-3 max-w-3xl text-3xl font-black leading-tight sm:text-5xl">
              Angenehme Luft vom ersten Moment an.
            </p>
          </div>
        </section>

        <section className="relative isolate min-h-[920px] overflow-hidden bg-[#f5f7fb] px-5 pb-32 pt-36 text-slate-950 sm:pt-44">
          <div className="absolute inset-0 -z-30 bg-[radial-gradient(circle_at_50%_16%,rgba(255,255,255,1),rgba(239,246,255,.82)_36%,rgba(248,250,252,.92)_68%,rgba(255,255,255,1))]" />
          <div className="klima-hero-orb absolute -left-24 top-16 -z-20 h-[28rem] w-[28rem] rounded-full bg-cyan-300/25 blur-[90px]" aria-hidden="true" />
          <div className="klima-hero-orb klima-hero-orb-slow absolute -right-24 top-56 -z-20 h-[32rem] w-[32rem] rounded-full bg-blue-400/20 blur-[110px]" aria-hidden="true" />

          <div className="mx-auto max-w-7xl">
            <div className="mx-auto max-w-6xl text-center" data-klima-reveal>
              <div className="klima-glass mb-7 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold text-sky-700">
                <MapPin className="h-4 w-4" aria-hidden="true" />
                Stuttgart & Umgebung
              </div>
              <h1 className="klima-display mx-auto max-w-6xl text-5xl font-black leading-[.98] sm:text-7xl lg:text-8xl">
                Klimaanlagen in Stuttgart – effizient kühlen und heizen
              </h1>
              <p className="mx-auto mt-8 max-w-3xl text-lg leading-8 text-slate-600 sm:text-2xl sm:leading-9">
                Von der ersten Idee bis zur sauberen Inbetriebnahme: Gravante Solar plant moderne Split- und
                Multi-Split-Lösungen für Wohnungen, Häuser und Gewerbe.
              </p>
              <div className="mt-10 flex flex-col justify-center gap-4 sm:flex-row">
                <a
                  href="/#kontakt"
                  className="klima-button inline-flex items-center justify-center gap-2 rounded-full bg-slate-950 px-8 py-4 font-bold text-white shadow-[0_14px_35px_rgba(15,23,42,.18)] hover:bg-sky-600 focus:outline-none focus:ring-4 focus:ring-sky-300/50"
                >
                  Klimaanlagen-Beratung anfragen
                  <ArrowRight className="h-5 w-5" aria-hidden="true" />
                </a>
                <a
                  href="tel:+4915782283963"
                  className="klima-button klima-glass inline-flex items-center justify-center gap-2 rounded-full px-8 py-4 font-bold text-slate-900 focus:outline-none focus:ring-4 focus:ring-sky-300/40"
                >
                  <Phone className="h-5 w-5" aria-hidden="true" />
                  01578 2283963
                </a>
              </div>
            </div>

            <div className="relative mx-auto mt-20 w-full max-w-5xl" data-klima-reveal aria-hidden="true">
              <div className="absolute inset-x-[10%] bottom-5 h-40 rounded-full bg-sky-300/35 blur-[65px]" />
              <div className="klima-glass relative overflow-hidden rounded-[2.8rem] p-4 sm:p-7">
                <div className="relative flex aspect-[16/8.4] min-h-[330px] items-center justify-center overflow-hidden rounded-[2.2rem] bg-[radial-gradient(circle_at_50%_10%,#ffffff_0%,#eff8ff_42%,#dff3ff_100%)]">
                  <div className="absolute left-[8%] top-[13%] h-40 w-40 rounded-full border border-white/80 bg-white/25 blur-sm" />
                  <div className="absolute bottom-[8%] right-[10%] h-28 w-28 rounded-full border border-sky-200/50 bg-sky-200/20" />
                  <div className="klima-unit relative w-[72%] max-w-2xl rounded-[1.35rem] border border-white bg-white px-8 py-8 shadow-[0_34px_85px_rgba(15,23,42,.20),inset_0_1px_0_rgba(255,255,255,1)] sm:px-14 sm:py-11">
                    <div className="h-2 rounded-full bg-gradient-to-r from-slate-100 via-slate-300 to-slate-100" />
                    <div className="mx-auto mt-7 h-1.5 w-2/3 rounded-full bg-gradient-to-r from-transparent via-sky-400 to-transparent" />
                    <div className="absolute right-7 top-5 h-2 w-2 rounded-full bg-emerald-400 shadow-[0_0_14px_rgba(52,211,153,.9)]" />
                    <div className="absolute inset-x-0 top-full">
                      <span className="klima-air-line" />
                      <span className="klima-air-line" />
                      <span className="klima-air-line" />
                    </div>
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-3 gap-3 text-center text-xs font-black uppercase tracking-[.16em] text-slate-600 sm:text-sm">
                  <span className="rounded-2xl bg-white/75 p-4">Leise</span>
                  <span className="rounded-2xl bg-white/75 p-4">Effizient</span>
                  <span className="rounded-2xl bg-white/75 p-4">Smart</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="relative -mt-16 px-5 pb-28">
          <div className="klima-glass mx-auto grid max-w-7xl gap-3 rounded-[2.2rem] p-4 sm:grid-cols-2 sm:p-5 lg:grid-cols-4" data-klima-reveal>
            {benefits.map(({ icon: Icon, title, text }, index) => (
              <article
                key={title}
                className="klima-lift rounded-[1.6rem] border border-transparent p-6 hover:border-sky-100 hover:bg-white/85"
                data-klima-reveal
                style={{ "--klima-delay": `${index * 70}ms` }}
              >
                <div className="mb-5 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-sky-100 to-cyan-50 shadow-inner">
                  <Icon className="h-6 w-6 text-sky-600" aria-hidden="true" />
                </div>
                <h2 className="text-lg font-extrabold text-slate-950">{title}</h2>
                <p className="mt-2 text-sm leading-6 text-slate-600">{text}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="px-5 py-28 sm:py-36">
          <div className="mx-auto max-w-7xl">
            <SectionHeading
              eyebrow="Individuell geplant"
              title="Klimaanlagen für Wohnung, Haus und Gewerbe"
              text="Nicht jedes Gebäude braucht dasselbe System. Wir planen nach Raumgröße, Nutzung, Schallschutz und Ihren Komfortwünschen."
            />
            <div className="grid gap-6 lg:grid-cols-3">
              {solutions.map(({ icon: Icon, eyebrow, title, text }, index) => (
                <article
                  key={title}
                  className="klima-glass klima-lift group rounded-[2rem] p-8 sm:p-10"
                  data-klima-reveal
                  style={{ "--klima-delay": `${index * 90}ms` }}
                >
                  <div className="mb-8 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-sky-100 text-sky-700 transition group-hover:bg-sky-600 group-hover:text-white">
                    <Icon className="h-7 w-7" aria-hidden="true" />
                  </div>
                  <p className="text-xs font-black uppercase tracking-[0.2em] text-sky-600">{eyebrow}</p>
                  <h3 className="mt-3 text-2xl font-black">{title}</h3>
                  <p className="mt-4 leading-7 text-slate-600">{text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="relative overflow-hidden bg-[#f5f5f7] px-5 py-28 sm:py-36">
          <div className="absolute left-1/2 top-1/3 h-[34rem] w-[34rem] -translate-x-1/2 rounded-full bg-sky-200/35 blur-[120px]" aria-hidden="true" />
          <div className="mx-auto max-w-7xl">
            <SectionHeading
              eyebrow="Markenlösungen"
              title="Daikin und Midea: passende Systeme für Ihren Bedarf"
              text="Wir vergleichen Funktionen, Leistung, Lautstärke und Budget – damit nicht die Marke, sondern die passende Lösung entscheidet."
            />

            <article className="klima-media-card klima-glass relative grid overflow-hidden rounded-[2.8rem] lg:grid-cols-2" data-klima-reveal="left">
              <div className="flex flex-col justify-center p-8 sm:p-12 lg:p-20">
                <p className="text-sm font-black uppercase tracking-[0.22em] text-blue-600">Daikin</p>
                <h3 className="klima-display mt-4 text-4xl font-black sm:text-5xl">Premium-Komfort, angenehm leise</h3>
                <p className="mt-5 text-lg leading-8 text-slate-600">
                  Daikin bietet durchdachte Single- und Multi-Split-Systeme für anspruchsvollen Wohnkomfort,
                  präzise Regelung und energieeffizienten Betrieb.
                </p>
                <ul className="mt-7 space-y-3">
                  {["Feine Temperaturregelung", "Leise Betriebsmodi", "Kühlen und – je nach Modell – heizen"].map((item) => (
                    <li key={item} className="flex items-start gap-3 text-slate-700">
                      <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-sky-600" aria-hidden="true" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="min-h-[360px] overflow-hidden bg-sky-100 lg:min-h-full">
                <img
                  src="/daikin-aria-fresca-homepage-animata.webp"
                  width="1672"
                  height="941"
                  loading="lazy"
                  decoding="async"
                  alt="Daikin Klimaanlage mit sanftem Luftstrom in einem modernen Wohnzimmer"
                  className="h-full w-full object-cover motion-reduce:hidden"
                />
                <div className="hidden h-full min-h-[320px] items-center justify-center bg-gradient-to-br from-white via-sky-50 to-cyan-100 p-10 text-center motion-reduce:flex">
                  <div>
                    <Wind className="mx-auto h-16 w-16 text-sky-600" aria-hidden="true" />
                    <p className="mt-5 text-xl font-black text-slate-900">Daikin Raumklima</p>
                    <p className="mt-2 text-slate-600">Sanfter Luftstrom und präziser Komfort.</p>
                  </div>
                </div>
              </div>
            </article>

            <article className="klima-media-card mt-10 grid overflow-hidden rounded-[2.8rem] bg-slate-950 text-white shadow-[0_34px_90px_rgba(2,6,23,.22)] lg:grid-cols-2" data-klima-reveal="right">
              <div className="relative min-h-[320px] overflow-hidden bg-gradient-to-br from-cyan-400 via-sky-500 to-blue-700 p-10 sm:p-14">
                <div className="absolute -right-16 -top-16 h-64 w-64 rounded-full border-[36px] border-white/10" />
                <div className="absolute -bottom-20 -left-20 h-72 w-72 rounded-full border-[42px] border-white/10" />
                <div className="relative flex h-full flex-col justify-between">
                  <Wind className="h-16 w-16 text-white/90" aria-hidden="true" />
                  <p className="max-w-sm text-3xl font-black leading-tight">Intelligente Klimatechnik mit starkem Preis-Leistungs-Verhältnis.</p>
                </div>
              </div>
              <div className="flex flex-col justify-center p-8 sm:p-12 lg:p-16">
                <p className="text-sm font-black uppercase tracking-[0.22em] text-cyan-300">Midea</p>
                <h3 className="klima-display mt-4 text-4xl font-black sm:text-5xl">Flexibel, smart und effizient</h3>
                <p className="mt-5 text-lg leading-8 text-slate-300">
                  Midea verbindet moderne Komfortfunktionen mit vielseitigen Lösungen für einzelne Räume und
                  mehrere Klimazonen – passend zu unterschiedlichen Budgets.
                </p>
                <ul className="mt-7 space-y-3">
                  {["Single- und Multi-Split-Auswahl", "Komfortable App-Steuerung", "Flexible Lösungen für viele Raumgrößen"].map((item) => (
                    <li key={item} className="flex items-start gap-3 text-slate-200">
                      <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-cyan-300" aria-hidden="true" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </article>
          </div>
        </section>

        <section className="px-5 py-28 sm:py-36">
          <div className="klima-glass mx-auto grid max-w-7xl items-center gap-12 overflow-hidden rounded-[2.8rem] bg-gradient-to-br from-emerald-50/90 to-cyan-50/90 p-8 sm:p-12 lg:grid-cols-[.8fr_1.2fr] lg:p-16" data-klima-reveal>
            <div className="relative flex h-64 items-center justify-center overflow-hidden rounded-[2rem] bg-white/85 shadow-[inset_0_1px_0_rgba(255,255,255,1),0_20px_50px_rgba(16,185,129,.10)]" data-klima-reveal="left">
              <div className="absolute -right-16 -top-16 h-48 w-48 rounded-full bg-emerald-200/50 blur-3xl" aria-hidden="true" />
              <div className="text-center">
                <CircleDollarSign className="mx-auto h-14 w-14 text-emerald-600" aria-hidden="true" />
                <p className="klima-display mt-5 text-4xl font-black text-slate-950 sm:text-5xl">30 % Basis · bis 70 %*</p>
                <p className="mt-1 text-sm font-bold uppercase tracking-widest text-emerald-700">mit anwendbaren Boni</p>
              </div>
            </div>
            <div data-klima-reveal="right">
              <p className="text-sm font-black uppercase tracking-[0.22em] text-emerald-700">Förderhinweis · Stand 05.09.2026</p>
              <h2 className="klima-display mt-4 text-4xl font-black leading-[1.02] sm:text-5xl">Förderung für Luft-Luft-Wärmepumpen</h2>
              <p className="mt-5 text-lg leading-8 text-slate-700">
                Reine Kühlanlagen sind in der Regel nicht förderfähig. Bestimmte Luft-Luft-Wärmepumpen können
                förderfähig sein, wenn sie überwiegend zum Heizen dienen und die technischen Voraussetzungen erfüllen.
              </p>
              <p className="mt-4 leading-7 text-slate-600">
                Die Basisförderung beträgt 30 %. Mit anwendbaren Boni kann die Gesamtförderung grundsätzlich bis zu
                70 % betragen. Bis zu 80 % sind nur in bestimmten
                Konstellationen für selbstnutzende Eigentümer mit anwendbaren Boni möglich. Die individuelle
                Förderfähigkeit muss vor der Beauftragung geprüft und der Antrag vor Beginn der Arbeiten gestellt werden.
              </p>
              <p className="mt-5 text-sm leading-6 text-slate-500">
                *Keine Förderzusage oder Rechtsberatung. Konditionen und technische Anforderungen können sich ändern.
              </p>
              <a href="/#kontakt" className="klima-button mt-7 inline-flex items-center gap-2 rounded-full bg-emerald-700 px-6 py-3.5 font-extrabold text-white shadow-lg shadow-emerald-900/10 hover:bg-emerald-600">
                Fördermöglichkeiten für Luft-Luft-Wärmepumpen prüfen
                <ArrowRight className="h-5 w-5" aria-hidden="true" />
              </a>
            </div>
          </div>
        </section>

        <section className="relative overflow-hidden bg-[#05070d] px-5 py-28 text-white sm:py-36">
          <div className="klima-hero-orb absolute -left-32 top-0 h-96 w-96 rounded-full bg-sky-500/10 blur-[100px]" aria-hidden="true" />
          <div className="klima-hero-orb klima-hero-orb-slow absolute -right-32 bottom-0 h-96 w-96 rounded-full bg-cyan-400/10 blur-[100px]" aria-hidden="true" />
          <div className="mx-auto max-w-7xl">
            <SectionHeading
              eyebrow="Einfach zum passenden System"
              title="So laufen Beratung, Planung und Montage ab"
              text="Ein klarer Ablauf sorgt für eine Anlage, die zum Gebäude passt und im Alltag überzeugt."
              light
            />
            <ol className="grid gap-6 lg:grid-cols-3">
              {steps.map((step, index) => (
                <li
                  key={step.number}
                  className="klima-dark-glass klima-lift relative rounded-[2rem] p-8 sm:p-10"
                  data-klima-reveal
                  style={{ "--klima-delay": `${index * 100}ms` }}
                >
                  <span className="klima-display text-6xl font-black text-cyan-300/25">{step.number}</span>
                  <div className="mt-8 flex items-center gap-3">
                    {index === 0 ? <ClipboardCheck aria-hidden="true" /> : index === 1 ? <Zap aria-hidden="true" /> : <Wrench aria-hidden="true" />}
                    <h3 className="text-xl font-black">{step.title}</h3>
                  </div>
                  <p className="mt-4 leading-7 text-slate-300">{step.text}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section className="px-5 py-28 sm:py-36">
          <div className="mx-auto max-w-4xl">
            <SectionHeading eyebrow="Gut zu wissen" title="Häufige Fragen zu Klimaanlagen" />
            <div className="klima-glass divide-y divide-slate-200/80 overflow-hidden rounded-[2.2rem]" data-klima-reveal>
              {faqs.map(({ question, answer }, index) => (
                <details key={question} className="group p-6 transition-colors open:bg-sky-50/70 sm:p-8" open={index === 0}>
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-6 text-lg font-extrabold text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500">
                    {question}
                    <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-sky-100 text-sky-700 transition group-open:rotate-45" aria-hidden="true">+</span>
                  </summary>
                  <p className="mt-5 max-w-3xl leading-7 text-slate-600">{answer}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="px-5 pb-28">
          <div className="relative mx-auto max-w-7xl overflow-hidden rounded-[2.8rem] bg-gradient-to-br from-[#087cf0] via-sky-600 to-cyan-500 px-7 py-16 text-white shadow-[0_32px_90px_rgba(2,132,199,.25)] sm:px-12 lg:flex lg:items-center lg:justify-between lg:px-16 lg:py-20" data-klima-reveal>
            <div className="klima-hero-orb absolute -right-24 -top-24 h-80 w-80 rounded-full border-[46px] border-white/10" aria-hidden="true" />
            <div>
              <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-white/15 px-4 py-2 text-sm font-bold">
                <ShieldCheck className="h-4 w-4" aria-hidden="true" />
                Persönlich geplant
              </div>
              <h2 className="klima-display max-w-3xl text-4xl font-black leading-[1.02] sm:text-5xl lg:text-6xl">Bereit für Ihr perfektes Raumklima?</h2>
              <p className="mt-4 max-w-2xl text-lg text-sky-50">Erzählen Sie uns kurz von Ihrem Objekt. Wir melden uns für eine unverbindliche Erstberatung.</p>
            </div>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row lg:mt-0 lg:flex-col">
              <a href="tel:+4915782283963" className="klima-button inline-flex items-center justify-center gap-2 rounded-full bg-white px-6 py-3.5 font-black text-sky-700 shadow-lg shadow-sky-900/10 hover:bg-sky-50">
                <Phone className="h-5 w-5" aria-hidden="true" />
                Jetzt anrufen
              </a>
              <a href="mailto:Buchhaltung@gravante.de" className="klima-button inline-flex items-center justify-center gap-2 rounded-full border border-white/35 bg-white/10 px-6 py-3.5 font-black text-white backdrop-blur hover:bg-white/15">
                <Mail className="h-5 w-5" aria-hidden="true" />
                E-Mail schreiben
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
